import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google';

const LoginContext = createContext(null);

export const LoginProvider = ({ onLoginSuccess, onLoginFailure, children }) => {
  // Process the access token (shared between popup and redirect flows)
  const processAccessToken = useCallback(async (accessToken) => {
    try {
      // Fetch user info from Google using the access token
      const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (!userInfoResponse.ok) {
        throw new Error('Failed to fetch user info');
      }

      const userInfo = await userInfoResponse.json();

      // Get our own auth token. ONLY the access token is sent: the server
      // verifies it against Google and derives the email itself (issue #69).
      // The userinfo call above happens in this browser and proves nothing to
      // the backend, so sending the email it returned would be the client
      // asserting its own identity — exactly the hole that was closed.
      const tokenResp = await fetch('https://api.1stprinciples.pro/auth/generateToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: accessToken })
      });

      if (tokenResp.ok) {
        const { token } = await tokenResp.json();
        localStorage.setItem('authToken', token);
      }

      // Note: Don't store googleToken - it's an access token, not a JWT
      // The authToken from our server is sufficient for session validation

      // Call the success handler with user data
      onLoginSuccess({
        email: userInfo.email,
        name: userInfo.name || userInfo.email.split('@')[0]
      });

      // Track login
      if (window.amplitude) {
        window.amplitude.track('User Login', {
          method: 'Google',
          email: userInfo.email
        });
      }
    } catch (error) {
      console.error('Login processing failed:', error);
      onLoginFailure?.(error);
    }
  }, [onLoginSuccess, onLoginFailure]);

  // Check for OAuth redirect response on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      // Parse the hash fragment
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');

      if (accessToken) {
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        // Process the token
        processAccessToken(accessToken);
      }
    }
  }, [processAccessToken]);

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    ux_mode: 'redirect',
    redirect_uri: window.location.origin,
    onSuccess: async (tokenResponse) => {
      await processAccessToken(tokenResponse.access_token);
    },
    onError: onLoginFailure,
  });

  return (
    <LoginContext.Provider value={{ triggerLogin: googleLogin }}>
      {children}
    </LoginContext.Provider>
  );
};

export const useLogin = () => {
  const context = useContext(LoginContext);
  if (!context) {
    // Return a no-op if used outside provider (graceful fallback)
    return { triggerLogin: () => console.warn('Login not available') };
  }
  return context;
};

export default LoginContext;
